import { useLazyQuery } from "@apollo/client/react";
import { useEffect, useMemo } from "react";

import { useCachedFilterFields } from "components/Filters/hooks";
import { SortOption } from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { WorkerPool } from "types/generated";

import { SEARCH_PRIVATE_WORKER_POOL_WORKERS_SUGGESTIONS } from "../gql";

type UseSearchPrivateWorkerPoolsSuggestions = {
  workerPoolId: string;
};

const useSearchPrivateWorkerPoolsSuggestions = ({
  workerPoolId,
}: UseSearchPrivateWorkerPoolsSuggestions) => {
  const { onError } = useTypedContext(FlashContext);

  //TODO: refactor to use useQuery hook, there is no need in this case to use useLazyQuery
  const [loadPrivateWorkerPoolWorkersSuggestions, { data }] = useLazyQuery<{
    workerPool: WorkerPool;
  }>(SEARCH_PRIVATE_WORKER_POOL_WORKERS_SUGGESTIONS);

  const rawSearchWorkersSuggestions = useMemo(() => {
    if (data?.workerPool) {
      const { searchWorkersSuggestions } = data.workerPool;
      return searchWorkersSuggestions;
    }

    return undefined;
  }, [data?.workerPool]);

  const cachedFiltersData = useCachedFilterFields(rawSearchWorkersSuggestions?.fields);

  const sortOptions = useMemo((): SortOption[] => {
    return cachedFiltersData
      .filter((field) => field.orderable)
      .map((field) => {
        return {
          value: field.name,
          label: field.name,
        };
      });
  }, [cachedFiltersData]);

  const loadSearchSuggestions = () => {
    loadPrivateWorkerPoolWorkersSuggestions({
      variables: {
        workerPoolId,
        input: {
          fullTextSearch: null,
          predicates: null,
          fields: null,
        },
      },
    }).catch((error) => {
      // Unmount/navigation mid-request aborts the pending query - not a real failure,
      // so don't surface it to the user.
      if (error?.name !== "AbortError") {
        onError(error);
      }
    });
  };

  useEffect(() => {
    loadSearchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sortOptions,
  };
};

export default useSearchPrivateWorkerPoolsSuggestions;
